import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { api } from "../../../api";
import { decodeTokenScopes } from "../../../auth/accessToken/helpers";
import authStore from "../../../auth/accessToken/observables/authStore";
import { useAccountId } from "../../../auth/useAccountId";
import useHasAnyOfScopes from "../../../common/hooks/useHasAnyOfScopes";
import { CHECKOUT_API_HOSTNAME, CORE_API_HOSTNAME } from "../../../env";
import type { CheckoutConfig } from "../../../types/checkout";
import type { Account, UpdateAccount } from "../../../types/management-auth";

type UseAccountParams = {
    onAdminUpdateAccountSuccess?: (account: Account) => void;
    accountId?: string;
    /** When this is passed, fetcher will trigger a call to partner account API, make sure the scopes are correct  */
    subAccountId?: string;
};

const updateCheckoutConfigMerchantDetails = async (
    accountId: string,
    account: Account,
) => {
    const tokens = authStore.select((state) => state.tokens);
    const token = tokens[accountId];
    const scopes = decodeTokenScopes(token);
    const canReadCheckoutConfig = ["admin:checkout", "read:checkout"].some(
        (scope) => scopes.includes(scope),
    );
    const canUpdateMerchantDetails = [
        "write:checkout:/admin/checkout",
        "admin:checkout",
    ].some((scope) => scopes.includes(scope));
    if (!canReadCheckoutConfig && !canUpdateMerchantDetails) {
        return;
    }
    // Fetch existing checkout config
    const checkoutConfig = await api<CheckoutConfig>({
        accountId: accountId,
        url: `${CHECKOUT_API_HOSTNAME}/v1/admin/checkout`,
    });
    if (canUpdateMerchantDetails && checkoutConfig) {
        // Update merchant details
        const updatedConfig: CheckoutConfig = {
            ...checkoutConfig,
            configuration: {
                ...checkoutConfig.configuration,
                merchant: {
                    ...checkoutConfig.configuration.merchant,
                    name:
                        account.company.public_business_name ||
                        checkoutConfig.configuration.merchant.name,
                    logo_url:
                        account.company.branding?.icon_url ||
                        checkoutConfig.configuration.merchant.logo_url,
                    website:
                        account.company.website ||
                        checkoutConfig.configuration.merchant.website,
                    email:
                        account.company.email ||
                        checkoutConfig.configuration.merchant.email,
                    phone_number:
                        account.company.phone_number ||
                        checkoutConfig.configuration.merchant.phone_number,
                    country:
                        account.company.address?.country ||
                        checkoutConfig.configuration.merchant.country,
                    postal_code:
                        account.company.address?.postal_code ||
                        checkoutConfig.configuration.merchant.postal_code,
                    postal_place:
                        account.company.address?.postal_place ||
                        checkoutConfig.configuration.merchant.postal_place,
                    address_line:
                        account.company.address?.address_line ||
                        checkoutConfig.configuration.merchant.address_line,
                    address_line_2:
                        account.company.address?.address_line_2 ||
                        checkoutConfig.configuration.merchant.address_line_2,
                },
            },
        };
        await api<CheckoutConfig, CheckoutConfig>({
            method: "PUT",
            accountId: accountId,
            url: `${CHECKOUT_API_HOSTNAME}/v1/admin/checkout`,
            json: updatedConfig,
        });
    }
};

const fetchAccount = async (key: string): Promise<Account> => {
    const [accountId, , subAccountId] = key.split("/");
    if (subAccountId) {
        return await api<Account>({
            accountId: accountId,
            url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/management/accounts/${subAccountId}`,
        });
    }
    return await api<Account>({
        accountId: accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/management/settings`,
        handlers: {
            200: (account) => {
                return account;
            },
        },
    });
};

const fetchUpdateAccount = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            account: UpdateAccount;
            updateCheckoutConfig?: boolean;
        };
    },
) => {
    const updatedAccount = await api<Account, UpdateAccount>({
        method: "PUT",
        accountId: options.arg.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.accountId}/management/settings`,
        json: options.arg.account,
        handlers: {
            200: (account: Account) => {
                authStore.dispatch("updateAuthenticatedUser", {
                    accountId: options.arg.accountId,
                });
                return account;
            },
        },
    });
    try {
        // Update the `merchant` details with the new account info
        if (updatedAccount && options.arg.updateCheckoutConfig) {
            await updateCheckoutConfigMerchantDetails(
                options.arg.accountId,
                updatedAccount,
            );
        }
    } catch (error) {
        console.error(
            "Failed to update checkout config merchant details:",
            error,
        );
    }
    return updatedAccount;
};

const fetchAdminUpdateAccount = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            partnerId: string;
            account: UpdateAccount;
        };
    },
) => {
    return api<Account, UpdateAccount>({
        method: "PUT",
        accountId: options.arg.partnerId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.partnerId}/management/accounts/${options.arg.accountId}`,
        json: options.arg.account,
        handlers: {
            200: (account: Account) => {
                return account;
            },
        },
    });
};

export default function useAccount(params?: UseAccountParams) {
    const sessionAccountId = useAccountId();
    const hasScopesForRead = useHasAnyOfScopes([
        "read:accounts",
        "admin:accounts",
    ]);
    const hasScopesForWrite = useHasAnyOfScopes([
        "write:accounts",
        "admin:accounts",
    ]);
    const accountId = params?.accountId || sessionAccountId;
    const key = params?.subAccountId
        ? `${accountId}/settings/${params.subAccountId}`
        : `${accountId}/settings`;
    const { data, error, isLoading, mutate } = useSWR(
        () => (accountId && hasScopesForRead ? key : null),
        fetchAccount,
    );
    const {
        trigger: updateAccount,
        isMutating: isUpdatingAccount,
        error: updateAccountError,
    } = useSWRMutation(
        () => (hasScopesForWrite ? key : null),
        fetchUpdateAccount,
        {
            onSuccess: (data: Account) => {
                mutate(data);
            },
        },
    );
    const {
        trigger: adminUpdateAccount,
        isMutating: isAdminUpdatingAccount,
        error: adminUpdateAccountError,
    } = useSWRMutation(
        () => (hasScopesForWrite ? key : null),
        fetchAdminUpdateAccount,
        {
            onSuccess: (data: Account) => {
                mutate(data);
                params?.onAdminUpdateAccountSuccess?.(data);
            },
        },
    );

    return {
        isLoading,
        isNotFound: !!error || !data,
        account: data,
        accountId,
        updateAccount,
        isUpdatingAccount,
        updateAccountError,
        adminUpdateAccount,
        isAdminUpdatingAccount,
        adminUpdateAccountError,
        mutate,
    };
}
